import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppNav } from "@/components/AppNav";
import { SelfieImage } from "@/components/SelfieImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { readFileAsDataURL } from "@/lib/month";
import { TeamPerformance } from "@/components/TeamPerformance";
import { MissingChannelsAlert } from "@/components/MissingChannelsAlert";
import { formatDateTime } from "@/lib/format";
import { FileText, Loader2, Send, Lock, Upload, Camera, X, Building2, User } from "lucide-react";

function NewReport({ onCreated, dark, properties }) {
  const [form, setForm] = useState({ title: "", summary: "", property_id: "", about_staff_id: "" });
  const [relType, setRelType] = useState("property");
  const [staffOptions, setStaffOptions] = useState([]);
  const [proof, setProof] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/reports/staff-options").then(({ data }) => setStaffOptions(data.staff)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (relType === "property" && !form.property_id) return toast.error("Pilih property dulu");
    if (relType === "staff" && !form.about_staff_id) return toast.error("Pilih staff dulu");
    setSaving(true);
    try {
      await api.post("/reports", {
        title: form.title,
        summary: form.summary,
        property_id: relType === "property" ? form.property_id : null,
        about_staff_id: relType === "staff" ? form.about_staff_id : null,
        proof,
      });
      toast.success("Report submitted — it is now locked");
      setForm({ title: "", summary: "", property_id: "", about_staff_id: "" });
      setProof(null);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setProof(await readFileAsDataURL(f));
  };

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Send className="h-5 w-5 text-indigo-600" />
          <h3 className="font-display font-semibold text-lg text-slate-900">Submit a report</h3>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Daily sales & OTA update" data-testid="report-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Terkait</Label>
            <div className="flex rounded-lg border border-slate-300 overflow-hidden w-fit mb-2" data-testid="report-related-toggle">
              <button type="button" onClick={() => setRelType("property")} className={`px-3 h-8 text-xs font-medium ${relType === "property" ? "bg-indigo-100 text-indigo-700" : "text-slate-500"}`} data-testid="report-related-property">Property</button>
              <button type="button" onClick={() => setRelType("staff")} className={`px-3 h-8 text-xs font-medium border-l border-slate-300 ${relType === "staff" ? "bg-teal-100 text-teal-700" : "text-slate-500"}`} data-testid="report-related-staff">Staff name</button>
            </div>
            {relType === "property" ? (
              <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="report-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>{(properties || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Select value={form.about_staff_id} onValueChange={(v) => setForm({ ...form, about_staff_id: v })}>
                <SelectTrigger data-testid="report-staff-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>{staffOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Summary</Label>
            <Textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} required className="resize-none h-28" placeholder="What did you accomplish today?" data-testid="report-summary-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Screenshot proof (optional)</Label>
            {proof ? (
              <div className="relative w-40">
                <img src={proof} alt="proof" className="rounded-lg border border-slate-200 aspect-video object-cover" />
                <button type="button" onClick={() => setProof(null)} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-rose-500 text-white flex items-center justify-center" data-testid="report-remove-proof-btn"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="report-upload-btn"><Upload className="h-4 w-4" /> Attach screenshot</Button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="report-file-input" />
          </div>
          <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <Lock className="h-3.5 w-3.5" /> Once submitted, a report is locked and cannot be edited.
          </div>
          <Button type="submit" className="w-full" disabled={saving} data-testid="submit-report-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Submit report</>}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function ReportItem({ r, showStaff, dark }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${dark ? "bg-slate-900 border-slate-800 hover:border-indigo-600" : "bg-white border-slate-200 hover:border-indigo-300"}`}
          data-testid={`report-item-${r.id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className={`font-medium ${dark ? "text-white" : "text-slate-900"}`}>{r.title}</div>
              {showStaff && <div className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>{r.staff_name}</div>}
              <p className={`text-sm mt-1 line-clamp-2 ${dark ? "text-slate-400" : "text-slate-600"}`}>{r.summary}</p>
            </div>
            {r.proof_path && <SelfieImage path={r.proof_path} className="h-14 w-14 rounded-lg object-cover flex-shrink-0" />}
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge variant="secondary" className="gap-1 text-[10px]"><Lock className="h-2.5 w-2.5" /> Locked</Badge>
            {r.property_name && <Badge variant="outline" className="gap-1 text-[10px]"><Building2 className="h-2.5 w-2.5" /> {r.property_name}</Badge>}
            {r.about_staff_name && <Badge variant="outline" className="gap-1 text-[10px]" data-testid={`report-about-staff-${r.id}`}><User className="h-2.5 w-2.5" /> {r.about_staff_name}</Badge>}
            <span className={`text-xs font-mono ${dark ? "text-slate-500" : "text-slate-400"}`}>{formatDateTime(r.report_date)}</span>
          </div>
        </motion.div>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{r.title}</DialogTitle>
          <DialogDescription>{r.staff_name} · {formatDateTime(r.report_date)}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-700 whitespace-pre-wrap">{r.summary}</p>
        {r.proof_path && <SelfieImage path={r.proof_path} className="w-full rounded-xl object-cover" />}
      </DialogContent>
    </Dialog>
  );
}

export default function ReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dark = isAdmin;
  const [reports, setReports] = useState([]);
  const [properties, setProperties] = useState([]);
  const [propFilter, setPropFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [rep, pr] = await Promise.all([api.get("/reports"), api.get("/properties")]);
      setReports(rep.data.reports);
      setProperties(pr.data.properties);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shownReports = propFilter === "all" ? reports : reports.filter((r) => r.property_id === propFilter);

  return (
    <div className={`min-h-screen ${dark ? "bg-[#1F1B16] dark-scroll text-slate-100" : "bg-slate-50"}`}>
      <AppNav />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className={`font-display text-2xl font-bold flex items-center gap-2 ${dark ? "text-white" : "text-slate-900"}`}>
              <FileText className="h-6 w-6 text-indigo-500" /> Reports
            </h1>
            <p className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>{isAdmin ? "All staff reports (read-only)" : "Submit and review your work reports"}</p>
          </div>
          {properties.length > 0 && (
            <Select value={propFilter} onValueChange={setPropFilter}>
              <SelectTrigger className={`w-52 ${dark ? "bg-slate-900 border-slate-700 text-slate-200" : ""}`} data-testid="report-property-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All properties</SelectItem>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {isAdmin && <MissingChannelsAlert dark={dark} />}
        {isAdmin && <TeamPerformance dark={dark} propertyId={propFilter} propertyName={properties.find((p) => p.id === propFilter)?.name} />}

        <div className={`grid gap-6 ${isAdmin ? "" : "lg:grid-cols-2 items-start"}`}>
          {!isAdmin && <NewReport onCreated={load} dark={dark} properties={properties} />}
          <div className="space-y-3">
            <h3 className={`text-sm font-semibold uppercase tracking-wide ${dark ? "text-slate-400" : "text-slate-500"}`}>
              {isAdmin ? "Submitted reports" : "My reports"}
            </h3>
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
            ) : shownReports.length === 0 ? (
              <p className={`text-sm py-8 text-center ${dark ? "text-slate-500" : "text-slate-400"}`}>No reports yet.</p>
            ) : (
              <div className={isAdmin ? "grid sm:grid-cols-2 lg:grid-cols-3 gap-3" : "space-y-3"} data-testid="reports-list">
                {shownReports.map((r) => <ReportItem key={r.id} r={r} showStaff={isAdmin} dark={dark} />)}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
